import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, ExternalLink, Newspaper, Tag, User } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NewsItem } from '@/types';
import SEO from '@/components/common/SEO';
import { formatContentForDisplay } from '@/utils/htmlUtils';
import { toSlug } from '@/lib/seo';

const CATEGORIES = ['Announcements', 'Updates', 'Features', 'Research', 'Community'];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const News = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('news')
          .select('*')
          .order('date', { ascending: false });

        if (error) throw error;
        setNewsItems(data || []);
      } catch (err) {
        console.error('Error loading news:', err);
        toast({
          title: 'Error loading news',
          description: 'Could not fetch news from the server.',
          variant: 'destructive',
        });
        setNewsItems([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchNews();
  }, [toast]);

  const filteredItems = activeCategory === 'All'
    ? newsItems
    : newsItems.filter((news) => news.category === activeCategory);

  const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const handleNewsClick = (newsId: string, title: string) => {
    navigate(`/news/${newsId}/${toSlug(title)}`);
  };

  return (
    <MainLayout>
      <SEO
        title="CanXP AI News — Launches, Research, and Sovereign AI Updates"
        description="Read the latest CanXP AI news, product launches, research updates, and analysis on sovereign Canadian AI, private deployment, and model training."
        keywords="CanXP AI news, sovereign AI news Canada, MaplePT news, private AI updates"
        url="/news"
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <div className="container mx-auto max-w-[1200px] px-4 py-12 md:py-16">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Newspaper size={28} className="text-primary" />
              <div>
                <h1 className="text-3xl font-bold md:text-4xl">News</h1>
                <p className="mt-2 text-muted-foreground">Announcements, product updates, research, and community stories from CanXP AI.</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border bg-muted/20 py-20 text-center">
              <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
              <h3 className="mt-6 text-xl font-medium">Loading news...</h3>
            </div>
          ) : newsItems.length === 0 ? (
            <div className="rounded-2xl border bg-muted/20 py-20 text-center">
              <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
              <h3 className="mt-6 text-xl font-medium">No news yet</h3>
              <p className="mt-2 text-muted-foreground">Check back later for updates about our platform.</p>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={activeCategory === 'All' ? 'default' : 'outline'}
                    className="cursor-pointer px-4 py-2 text-sm"
                    onClick={() => handleCategoryChange('All')}
                  >
                    All
                  </Badge>
                  {CATEGORIES.map((category) => (
                    <Badge
                      key={category}
                      variant={activeCategory === category ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-2 text-sm"
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {paginatedItems.map((news) => (
                  <Card key={news.id} className="flex h-full flex-col overflow-hidden border-border/70 bg-background/80 shadow transition-shadow hover:shadow-md">
                    {news.image_url ? (
                      <div className="relative cursor-pointer" onClick={() => handleNewsClick(news.id, news.title)}>

                        <Badge className="absolute left-3 top-3 z-10">{news.category || 'General'}</Badge>
                        <div className="group relative">
                          <img
                            src={news.image_url}
                            alt={news.title}
                            className="aspect-video w-full object-cover transition-opacity group-hover:opacity-90"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
                            <ExternalLink className="text-white" size={24} />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="flex flex-1 flex-col p-6">
                      <CardHeader className="p-0 pb-4">
                        <CardTitle
                          className="line-clamp-2 cursor-pointer text-xl transition-colors hover:text-primary"
                          onClick={() => handleNewsClick(news.id, news.title)}

                        >
                          {news.title}
                        </CardTitle>
                        <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            <span>{formatDate(news.date)}</span>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="flex-grow p-0 pb-4">
                        <div className="mt-2 line-clamp-3 text-muted-foreground" dangerouslySetInnerHTML={{ __html: formatContentForDisplay(news.content) }} />
                      </CardContent>

                      <CardFooter className="flex items-center justify-between p-0 pt-2">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User size={12} className="text-muted-foreground" />
                          <span>{news.author.split(',')[0]}</span>
                        </div>
                        <Button variant="link" className="px-0 text-sm font-medium text-primary" onClick={() => handleNewsClick(news.id, news.title)}>

                          Read more
                        </Button>
                      </CardFooter>
                    </div>
                  </Card>
                ))}
              </div>

              {totalPages > 1 ? (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }).map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1
                          || page === totalPages
                          || page === currentPage
                          || page === currentPage - 1
                          || page === currentPage + 1
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink
                                isActive={page === currentPage}
                                onClick={() => handlePageChange(page)}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }

                        if (
                          (page === 2 && currentPage > 3)
                          || (page === totalPages - 1 && currentPage < totalPages - 2)
                        ) {
                          return (
                            <PaginationItem key={`ellipsis-${page}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }

                        return null;
                      })}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              ) : null}

              {paginatedItems.length === 0 && filteredItems.length > 0 ? (
                <div className="mt-8 rounded-2xl border bg-muted/10 py-12 text-center">
                  <Tag className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
                  <h3 className="mt-4 text-xl font-medium">No news for this filter</h3>
                  <p className="mt-2 text-muted-foreground">Try selecting a different category</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleCategoryChange('All')}>
                    View all stories
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default News;
